import { logger } from '../../utils/logger.js'
import { LitElement, html, css } from 'lit'
import { customElement, property, state, query } from 'lit/decorators.js'
import Fuse from 'fuse.js'

const log = logger.create('help')

interface SearchResult {
  id: string
  title: string
  content: string
  category: string
  url?: string | undefined
  score?: number | undefined
}

/**
 * Help search component with offline-first fuzzy search
 *
 * @fires search-query - Fired when search query changes
 * @fires result-selected - Fired when a search result is selected
 * @fires search-cleared - Fired when search is cleared
 *
 * @slot no-results - Content to show when no results are found
 * @slot loading - Content to show while searching
 *
 * @csspart search-container - The search input container
 * @csspart search-input - The search input field
 * @csspart results-container - The search results container
 * @csspart result-item - Individual search result item
 */
@customElement('cairn-help-search')
export class CairnHelpSearch extends LitElement {
  static override styles = css`
    :host {
      display: block;
      font-family: var(
        --cairn-font-family,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        Roboto,
        sans-serif
      );
      /* Add bottom padding for mobile navigation */
      padding-bottom: calc(var(--cairn-spacing-lg, 24px) + 60px);
    }

    .search-container {
      position: relative;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .search-input {
      width: 100%;
      padding: var(--cairn-spacing-md, 16px);
      border: 2px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      font-size: var(--cairn-text-size-md, 16px);
      font-family: inherit;
      background: var(--cairn-input-background, #ffffff);
      color: var(--cairn-text-color, #1e293b);
      transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;
    }

    .search-input:focus {
      outline: none;
      border-color: var(--cairn-primary-color, #3b82f6);
      box-shadow: 0 0 0 3px var(--cairn-primary-color-alpha, rgba(59, 130, 246, 0.1));
    }

    .search-input::placeholder {
      color: var(--cairn-text-placeholder-color, #94a3b8);
    }

    .search-icon {
      position: absolute;
      right: var(--cairn-spacing-md, 16px);
      top: 50%;
      transform: translateY(-50%);
      color: var(--cairn-text-secondary-color, #64748b);
      pointer-events: none;
    }

    .clear-button {
      position: absolute;
      right: var(--cairn-spacing-sm, 8px);
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      padding: var(--cairn-spacing-sm, 8px);
      border-radius: var(--cairn-border-radius-sm, 4px);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .clear-button:hover {
      background: var(--cairn-surface-variant-color, #f1f5f9);
    }

    .results-container {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      box-shadow: var(--cairn-shadow-lg, 0 4px 6px rgba(0, 0, 0, 0.1));
      max-height: 400px;
      overflow-y: auto;
    }

    .result-item {
      padding: var(--cairn-spacing-md, 16px);
      border-bottom: 1px solid var(--cairn-border-color-light, #f1f5f9);
      cursor: pointer;
      transition: background-color 0.2s ease;
    }

    .result-item:last-child {
      border-bottom: none;
    }

    .result-item:hover,
    .result-item:focus {
      background: var(--cairn-surface-variant-color, #f8fafc);
      outline: none;
    }

    .result-item.selected {
      background: var(--cairn-primary-color-alpha, rgba(59, 130, 246, 0.1));
    }

    .result-title {
      font-weight: var(--cairn-font-weight-medium, 500);
      color: var(--cairn-text-primary-color, #0f172a);
      margin: 0 0 var(--cairn-spacing-xs, 4px) 0;
      font-size: var(--cairn-text-size-md, 16px);
    }

    .result-content {
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 0 0 var(--cairn-spacing-xs, 4px) 0;
      font-size: var(--cairn-text-size-sm, 14px);
      line-height: 1.4;
    }

    .result-category {
      font-size: var(--cairn-text-size-xs, 12px);
      color: var(--cairn-text-tertiary-color, #94a3b8);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-weight: var(--cairn-font-weight-medium, 500);
    }

    .no-results {
      padding: var(--cairn-spacing-xl, 32px);
      text-align: center;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .loading {
      padding: var(--cairn-spacing-xl, 32px);
      text-align: center;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .offline-notice {
      padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 16px);
      background: var(--cairn-warning-color-light, #fef3c7);
      color: var(--cairn-warning-color-dark, #92400e);
      font-size: var(--cairn-text-size-sm, 14px);
      border-radius: var(--cairn-border-radius, 8px);
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    /* Highlight search terms */
    .highlight {
      background: var(--cairn-highlight-background, #fbbf24);
      color: var(--cairn-highlight-text, #000000);
      padding: 0 2px;
      border-radius: 2px;
    }

    /* Dark mode support */
    @media (prefers-color-scheme: dark) {
      .search-input {
        background: var(--cairn-input-background-dark, #334155);
        border-color: var(--cairn-border-color-dark, #475569);
        color: var(--cairn-text-color-dark, #f1f5f9);
      }

      .results-container {
        background: var(--cairn-surface-color-dark, #1e293b);
        border-color: var(--cairn-border-color-dark, #475569);
      }

      .result-item:hover,
      .result-item:focus {
        background: var(--cairn-surface-variant-color-dark, #374151);
      }
    }

    /* High contrast mode */
    @media (prefers-contrast: high) {
      .search-input {
        border-width: 3px;
      }

      .result-item {
        border-bottom-width: 2px;
      }
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .search-input,
      .result-item {
        transition: none;
      }
    }
  `

  @property({ type: Array }) searchData: SearchResult[] = []
  @property({ type: String }) placeholder: string = 'Search help articles...'
  @property({ type: Boolean }) offlineMode: boolean = false
  @property({ type: Number }) debounceMs: number = 300
  @property({ type: Number }) maxResults: number = 10

  @state() private _query: string = ''
  @state() private _results: SearchResult[] = []
  @state() private _isSearching: boolean = false
  @state() private _selectedIndex: number = -1
  @state() private _showResults: boolean = false

  @query('.search-input') private _searchInput?: HTMLInputElement

  private _fuse?: Fuse<SearchResult>
  private _debounceTimer?: number

  override connectedCallback(): void {
    super.connectedCallback()
    this._initializeFuse()
    document.addEventListener('click', this._handleDocumentClick)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    document.removeEventListener('click', this._handleDocumentClick)
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer)
    }
  }

  private _initializeFuse(): void {
    this._fuse = new Fuse(this.searchData, {
      keys: [
        { name: 'title', weight: 0.4 },
        { name: 'content', weight: 0.3 },
        { name: 'category', weight: 0.3 },
      ],
      threshold: 0.4,
      includeScore: true,
      includeMatches: true,
    })
  }

  private _handleDocumentClick = (event: Event): void => {
    if (!this.contains(event.target as Node)) {
      this._showResults = false
    }
  }

  private _handleInput(event: Event): void {
    const input = event.target as HTMLInputElement
    this._query = input.value

    this.dispatchEvent(
      new CustomEvent('search-query', {
        detail: { query: this._query },
        bubbles: true,
      })
    )

    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer)
    }

    this._debounceTimer = window.setTimeout((): void => {
      void this._performSearch()
    }, this.debounceMs)
  }

  private async _performSearch(): Promise<void> {
    if (!this._query.trim()) {
      this._results = []
      this._showResults = false
      return
    }

    this._isSearching = true

    try {
      if (navigator.onLine && !this.offlineMode) {
        // Online search - could hit API for fresh results
        await this._searchOnline()
      } else {
        // Offline search using Fuse.js
        this._searchOffline()
      }
    } finally {
      this._isSearching = false
      this._selectedIndex = -1
      this._showResults = true
    }
  }

  private async _searchOnline(): Promise<void> {
    // Fallback to offline search if online search fails
    try {
      // Simulate online search - in real implementation, this would be an API call
      await new Promise<void>(resolve => setTimeout(resolve, 200))
      this._searchOffline()
    } catch (error) {
      log.warn('Online search failed, falling back to offline:', error)
      this._searchOffline()
    }
  }

  private _searchOffline(): void {
    if (!this._fuse) {
      this._initializeFuse()
    }

    const fuseResults = this._fuse!.search(this._query, { limit: this.maxResults })
    this._results = fuseResults.map(result => ({
      ...result.item,
      score: result.score,
    }))
  }

  private _handleKeydown(event: KeyboardEvent): void {
    if (!this._showResults || this._results.length === 0) {
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this._selectedIndex = Math.min(this._selectedIndex + 1, this._results.length - 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this._selectedIndex = Math.max(this._selectedIndex - 1, -1)
        break
      case 'Enter':
        event.preventDefault()
        if (this._selectedIndex >= 0) {
          this._selectResult(this._results[this._selectedIndex])
        }
        break
      case 'Escape':
        event.preventDefault()
        this._clearSearch()
        break
    }
  }

  private _selectResult(result: SearchResult): void {
    this.dispatchEvent(
      new CustomEvent('result-selected', {
        detail: { result },
        bubbles: true,
      })
    )
    this._showResults = false
  }

  private _clearSearch(): void {
    this._query = ''
    this._results = []
    this._showResults = false
    this._selectedIndex = -1
    if (this._searchInput) {
      this._searchInput.value = ''
      this._searchInput.focus()
    }

    this.dispatchEvent(
      new CustomEvent('search-cleared', {
        bubbles: true,
      })
    )
  }

  protected override updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('searchData')) {
      this._initializeFuse()
    }
  }

  protected override render(): unknown {
    return html`
      ${this.offlineMode
        ? html` <div class="offline-notice">🔍 Searching cached content only</div> `
        : ''}

      <div class="search-container" part="search-container">
        <input
          type="text"
          class="search-input"
          part="search-input"
          .value="${this._query}"
          placeholder="${this.placeholder}"
          @input="${this._handleInput}"
          @keydown="${this._handleKeydown}"
          @focus="${(): boolean => (this._showResults = true)}"
          autocomplete="off"
          role="combobox"
          aria-expanded="${this._showResults}"
          aria-haspopup="listbox"
        />

        ${this._query
          ? html`
              <button
                type="button"
                class="clear-button"
                @click="${this._clearSearch}"
                aria-label="Clear search"
              >
                ✕
              </button>
            `
          : html` <div class="search-icon">🔍</div> `}
      </div>

      ${this._showResults
        ? html`
            <div class="results-container" part="results-container" role="listbox">
              ${this._isSearching
                ? html`
                    <div class="loading">
                      <slot name="loading">Searching...</slot>
                    </div>
                  `
                : this._results.length === 0
                  ? html`
                      <div class="no-results">
                        <slot name="no-results"> No results found for "${this._query}" </slot>
                      </div>
                    `
                  : this._results.map(
                      (result, index) => html`
                        <div
                          class="result-item ${index === this._selectedIndex ? 'selected' : ''}"
                          part="result-item"
                          @click="${(): void => this._selectResult(result)}"
                          role="option"
                          aria-selected="${index === this._selectedIndex}"
                          tabindex="-1"
                        >
                          <h3 class="result-title">${result.title}</h3>
                          <p class="result-content">${result.content}</p>
                          <div class="result-category">${result.category}</div>
                        </div>
                      `
                    )}
            </div>
          `
        : ''}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-help-search': CairnHelpSearch
  }
}
